import React from 'react';
import { CarResponseData } from '../../../../types/VehicleResponse';
import styled, { ThemeConsumer } from 'styled-components';
import VehicleInfoBadge from '../components/VehicleInfoBadge';
import CarCategoryIconSelector from '../components/CarCategoryIconSelector';
import { useTranslation } from 'react-i18next';

interface Props {
  // eslint-disable-next-line
  data: CarResponseData | any;
  onWrong?: () => void;
  isVin?: boolean;
}

const VehicleResponseHeaderContainer: React.FC<
  React.PropsWithChildren<Props>
> = ({ data, onWrong, isVin }) => {
  const { t } = useTranslation();
  const { images, physical_properties, general } = data;
  return (
    <ThemeConsumer>
      {(theme) => (
        <Container>
          <StyledImg
            src={images?.[0]}
            alt="Picture of car"
            data-test-id="Tk_VehicleResponseHeaderContaienr_image"
          />
          {isVin && (
            <Wrong
              data-test-id="Tk_VehicleResponseHeaderContaienr_wrongButton"
              onClick={onWrong}
            >
              {t('Wrong vehicle?')}
            </Wrong>
          )}
          <OverlappingContainer>
            <StyledVehicleInfoBadge
              physicalData={physical_properties || {}}
              generalData={general}
            />
          </OverlappingContainer>
          <CarTypeBadge>
            <IconContainer>
              <CarCategoryIconSelector
                category={physical_properties?.category}
                color={theme.brand.primary}
              />
            </IconContainer>
            <p>
              {physical_properties?.category
                ? t(`ToolkitInfo.category.${physical_properties?.category}`)
                : t('Category unknown')}
            </p>
          </CarTypeBadge>
        </Container>
      )}
    </ThemeConsumer>
  );
};

const CarTypeBadge = styled.div`
  background-color: white;
  line-height: 120%;
  border: 1px solid ${({ theme }) => theme.gray[100]};
  padding: 1.6rem;
  display: none;
  align-items: center;
  max-width: 32rem;
  position: absolute;
  bottom: 2.4rem;
  right: 2.4rem;
  min-height: 7.6rem;
  min-width: 20rem;
  border-radius: 0.5rem;
  align-self: end;

  @media print {
    p {
      font-size: 14px;
    }
  }
`;

const IconContainer = styled.div`
  width: 4.4rem;
  height: 4.4rem;
  border-radius: 4.4rem;
  background-color: ${({ theme }) => theme.brand.lightest};
  margin-right: 2.4rem;
  display: flex;
  justify-content: center;
  align-items: center;

  & > svg {
    width: 100%;
  }

  @media print {
    width: 5rem;
    height: 5rem;

    > svg {
      width: 80%;
    }
  }
`;

const StyledVehicleInfoBadge = styled(VehicleInfoBadge)`
  align-self: end;
`;

const OverlappingContainer = styled.div`
  position: absolute;
  padding: 2.4rem;
  bottom: 0;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  width: 100%;
`;

const StyledImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  top: 0;
  left: 0;
`;

const Wrong = styled.p`
  background-color: black;
  padding: 0.8rem;
  display: inline-block;
  color: white;
  border-radius: 0.5rem;
  cursor: pointer;
  position: absolute;
  right: 2.4rem;
  top: 2.4rem;
  @media print {
    display: none;
  }
`;

const Container = styled.div`
  position: relative;
  height: 36rem;
  border: 1px solid ${({ theme }) => theme.gray[100]};
  border-radius: 0.5rem;
  overflow: hidden;
`;

export default VehicleResponseHeaderContainer;
